import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { documentTypeSectionsPreview } from '../../../core/services/resources';
import type { DocumentCategory, DocumentType, DocumentTypeSection, EntityType } from '../../../core/types/data-models';

// ─── hierarchy helper ──────────────────────────────────────────────────────

interface SectionNode {
  section: DocumentTypeSection;
  children: SectionNode[];
}

function buildSectionTree(sections: DocumentTypeSection[], parentType: string | null = null): SectionNode[] {
  return sections
    .filter((s) => (s.parentDocumentTypeSectionType ?? null) === parentType)
    .sort((a, b) => (a.defaultOrder ?? Infinity) - (b.defaultOrder ?? Infinity))
    .map((s) => ({ section: s, children: buildSectionTree(sections, s.type) }));
}

interface DocumentTree {
  docType: DocumentType;
  tree: SectionNode[];
  entityTypes: EntityType[];
}

// ─── JSON representation ────────────────────────────────────────────────────

interface SectionJson {
  type: string;
  title: string;
  description: string | null;
  relatedSections: [];
  subSections?: SectionJson[];
}

function mapSectionJson(node: SectionNode): SectionJson {
  const result: SectionJson = {
    type: node.section.type,
    title: node.section.title,
    description: node.section.description,
    relatedSections: [],
  };
  if (node.children.length > 0) {
    result.subSections = node.children.map(mapSectionJson);
  }
  return result;
}

function buildDocumentJson(docType: DocumentType, tree: SectionNode[], entityTypes: EntityType[]) {
  return {
    type: docType.type,
    name: docType.name,
    order: docType.order,
    description: docType.description,
    relatedSections: [] as string[],
    sections: tree.map(mapSectionJson),
    entityTypes: entityTypes
      .filter((et) => et.llmPrompt)
      .map((et) => ({ name: et.name, description: et.description, llmPrompt: et.llmPrompt })),
  };
}

function buildJsonData(documents: DocumentTree[]) {
  const docs = documents.map(({ docType, tree, entityTypes }) => buildDocumentJson(docType, tree, entityTypes));
  return docs.length === 1 ? docs[0] : docs;
}

function buildCategoryJson(category: DocumentCategory, documents: DocumentTree[]) {
  return {
    type: category.type,
    name: category.name,
    order: category.order,
    description: category.description,
    documentTypes: documents.map(({ docType, tree, entityTypes }) => buildDocumentJson(docType, tree, entityTypes)),
  };
}

// ─── Markdown representation ─────────────────────────────────────────────────

function buildEntityTypesMarkdown(entityTypes: EntityType[]): string {
  const withPrompt = entityTypes.filter((et) => et.llmPrompt?.trim());
  if (withPrompt.length === 0) return '';
  const blocks = withPrompt.map((et) => `### ${et.name}\n${et.llmPrompt!.trim()}`);
  return `## Entity Types\n\n${blocks.join('\n\n')}`;
}

// The sections block itself is fetched from the backend (`documentTypeSectionsPreview`)
// rather than rebuilt here, so this preview always matches the real
// {DOCUMENT_SECTIONS} text generate-document.job.ts sends to the LLM.
function buildDocumentMarkdown(docType: DocumentType, sectionsMarkdown: string, entityTypes: EntityType[]): string {
  const intro = `# ${docType.name}\n${(docType.llmPrompt ?? '').trim()}`;
  return [intro, sectionsMarkdown, buildEntityTypesMarkdown(entityTypes)].filter(Boolean).join('\n\n');
}

function buildMarkdownText(documents: DocumentTree[], sectionsMarkdownByType: Map<string, string>): string {
  return documents
    .map(({ docType, entityTypes }) => buildDocumentMarkdown(docType, sectionsMarkdownByType.get(docType.type) ?? '', entityTypes))
    .join('\n\n');
}

function buildCategoryMarkdown(
  category: DocumentCategory,
  documents: DocumentTree[],
  sectionsMarkdownByType: Map<string, string>,
): string {
  const header = `# ${category.name}`;
  const body = buildMarkdownText(documents, sectionsMarkdownByType);
  return body ? `${header}\n\n${body}` : header;
}

function buildGroupedMarkdownText(groups: CategoryGroup[], sectionsMarkdownByType: Map<string, string>): string {
  return groups
    .map(({ category, documents }) => buildCategoryMarkdown(
      category,
      documents.map(({ docType, sections, entityTypes }) => ({ docType, tree: buildSectionTree(sections), entityTypes: entityTypes ?? [] })),
      sectionsMarkdownByType,
    ))
    .join('\n\n---\n\n');
}

// ─── Dialog ───────────────────────────────────────────────────────────────────

interface DocumentEntry {
  docType: DocumentType;
  /** Sections belonging to this document type (any depth). */
  sections: DocumentTypeSection[];
  /** Entity types linked to this document type, whose LLM prompts are included in the preview. */
  entityTypes?: EntityType[];
}

interface CategoryGroup {
  category: DocumentCategory;
  documents: DocumentEntry[];
}

type DocumentTypeDetailsDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
} & (
  | { documents: DocumentEntry[]; categoryGroups?: undefined }
  | { categoryGroups: CategoryGroup[]; documents?: undefined }
);

export function DocumentTypeDetailsDialog({ open, onClose, title, documents, categoryGroups }: DocumentTypeDetailsDialogProps) {
  const [tab, setTab] = useState<'markdown' | 'json'>('markdown');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setTab('markdown');
      setCopied(false);
    }
  }, [open]);

  const flatDocuments = useMemo(
    () => categoryGroups ? categoryGroups.flatMap((g) => g.documents) : documents ?? [],
    [categoryGroups, documents],
  );

  const documentTrees = useMemo(
    () => flatDocuments.map(({ docType, sections, entityTypes }) => ({ docType, tree: buildSectionTree(sections), entityTypes: entityTypes ?? [] })),
    [flatDocuments],
  );

  const previewQueries = useQueries({
    queries: flatDocuments.map(({ docType }) => ({
      queryKey: ['document-type-sections-preview', docType.type],
      queryFn: () => documentTypeSectionsPreview.get(docType.type),
      enabled: open,
      staleTime: 30_000,
    })),
  });
  const loadingPreview = previewQueries.some((q) => q.isLoading);
  const sectionsMarkdownByType = new Map<string, string>();
  flatDocuments.forEach(({ docType }, i) => sectionsMarkdownByType.set(docType.type, previewQueries[i]?.data ?? ''));

  const markdownText = categoryGroups
    ? buildGroupedMarkdownText(categoryGroups, sectionsMarkdownByType)
    : buildMarkdownText(documentTrees, sectionsMarkdownByType);

  const jsonText = useMemo(() => {
    if (categoryGroups) {
      const treeByType = new Map(documentTrees.map((d) => [d.docType.type, d]));
      const grouped = categoryGroups.map(({ category, documents: docs }) => buildCategoryJson(
        category,
        docs.map(({ docType }) => {
          const d = treeByType.get(docType.type);
          return { docType, tree: d?.tree ?? [], entityTypes: d?.entityTypes ?? [] };
        }),
      ));
      return JSON.stringify(grouped, null, 2);
    }
    return JSON.stringify(buildJsonData(documentTrees), null, 2);
  }, [categoryGroups, documentTrees]);

  const activeContent = tab === 'markdown' ? markdownText : jsonText;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(activeContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: '85vh', display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <span>{title} — Details</span>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ minHeight: 36 }}>
            <Tab value="markdown" label="Markdown" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36, py: 0.5 }} />
            <Tab value="json" label="JSON" sx={{ textTransform: 'none', fontWeight: 600, minHeight: 36, py: 0.5 }} />
          </Tabs>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ flex: '1 1 0', minHeight: 0, overflow: 'auto' }}>
        {tab === 'markdown' && loadingPreview ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={24} /></Box>
        ) : (
          <Box
            component="pre"
            sx={{
              m: 0,
              fontSize: 13,
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.6,
            }}
          >
            {activeContent}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
          {copied ? 'Copied!' : 'Copy'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
